import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    RefreshCw,
    ShoppingCart,
    Package,
    BarChart,
    Zap,
    Layers,
    Shield,
    Smartphone,
    ChevronRight,
} from "lucide-react";
import '../style/products/quantofyP.css';
import Header from "@/common/Header";
import logo from '../img/quanto_light.webp';
import Footer from "@/common/Footer";

const features = [
    { title: "Real-Time Integration", description: "For Quanto-integrated clients, Quantofyy syncs inventory, orders, and customer data automatically, ensuring accuracy and up-to-date information across platforms.", icon: <RefreshCw />, image: 'retailquantofy.webp' },
    { title: "Perfect for All Retailers", description: "Quantofyy is designed for both Quanto users and retail businesses across the industry, making it a versatile e-commerce solution that supports any retail business model.", icon: <ShoppingCart />, image: 'retailquantofy2.webp' },
    { title: "Easy Product Management", description: "Manage your product catalog with ease, from descriptions and pricing to stock levels. Bulk uploads and detailed attributes help streamline large inventories.", icon: <Package />, image: 'retailquantofy3.webp' },
    { title: "Advanced Analytics", description: "Access actionable insights into customer behavior and sales performance with comprehensive analytics tools. Use customizable reports to make data-driven decisions.", icon: <BarChart />, image: 'retailquantofy4.webp' },
    { title: "Personalized Shopping", description: "Enhance customer engagement with personalized recommendations, targeted promotions, and tailored loyalty programs to drive conversions and repeat business.", icon: <Zap />, image: 'retailquantofy5.webp' },
    { title: "Omnichannel Capabilities", description: "Sell across multiple channels like your website, mobile app, or social media platforms, and manage everything from a single, easy-to-use interface.", icon: <Layers />, image: 'retailquantofy6.webp' },
    { title: "Secure Payments", description: "Offer a variety of secure payment methods to ensure a safe checkout experience for customers, helping to build trust and increase sales.", icon: <Shield />, image: 'retailquantofy7.webp' },
    { title: "Mobile-Responsive", description: "Quantofyy's responsive design ensures your store looks great and functions smoothly across all devices, providing a consistent shopping experience.", icon: <Smartphone />, image: 'retailquantofy8.png' },
];


export default function QuantofyFeatures() {
    const [activeFeature, setActiveFeature] = useState(0);

    return (

        <>
            <Header></Header>
            <section className="about-title-section">
                <h2><img alt="quantologo" src={logo} style={{ width: 280, marginLeft: 10, verticalAlign: "middle", marginBottom: 20 }} /> Quantofy</h2>
            </section>
            <div className="quantofy-container">
                <div className="quantofy-content">
                    <div className="quantofy-title-container">
                        <h1 className="quantofy-main-title">Quantofyy: The Ultimate E-Commerce Solution for Retail Businesses</h1>
                        <p className="quantofy-subtitle">
                            Quantofyy is a powerful e-commerce platform that seamlessly integrates with Quanto ERP, offering retailers a complete solution for managing online operations and boosting sales.
                            Whether you're a Quanto client or from the broader retail industry, Quantofy ensures smooth synchronization with backend systems for efficient, real-time management.
                        </p>
                    </div>

                    <div className="quantofy-features-card">
                        <h2 className="quantofy-section-title">Key Features and Benefits</h2>
                        <div className="quantofy-features-layout">
                            <div className="quantofy-feature-list">
                                {features.map((feature, index) => (
                                    <motion.div
                                        key={index}
                                        className={`quantofy-feature-item ${activeFeature === index ? "active" : ""}`}
                                        onClick={() => setActiveFeature(index)}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <div className="quantofy-feature-icon">{feature.icon}</div>
                                        <span className="quantofy-feature-title">{feature.title}</span>
                                    </motion.div>
                                ))}
                            </div>
                            <div className="quantofy-feature-details">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={activeFeature}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.3 }}
                                        className="quantofy-feature-content"
                                    >
                                        <h4 className="quantofy-feature-detail-title">{features[activeFeature].title}</h4>
                                        <p className="quantofy-feature-description">{features[activeFeature].description}</p>
                                        <img
                                            src={require(`../img/product-quantofy/${features[activeFeature].image}`)}
                                            alt={features[activeFeature].title}
                                            className="quantofy-feature-image"
                                        />
                                        {/* <button className="quantofy-learn-more-btn">
                    Learn More
                    <ChevronRight className="quantofy-btn-icon" />
                </button> */}
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>

                    <div className="quantofy-why-choose-section">
                        <h4 className="quantofy-section-title">Why Choose Quantofyy?</h4>
                        <p className="quantofy-why-choose-description">
                            Designed for both Quanto-integrated clients and retailers across the industry, Quantofy offers a seamless bridge between in-store and online retail. With real-time updates, powerful features, and a focus on efficiency, Quantofyy helps you stay ahead in the competitive retail market.
                        </p>
                        <button className="quantofy-discover-more-btn">
                            Discover More
                        </button>
                    </div>

                    <div className="quantofy-cta-section">
                        <h2 className="quantofy-cta-heading">Ready to transform your retail business?</h2>
                        <div className="quantofy-cta-buttons">
                            <button className="quantofy-cta-get-started-btn">Get Started</button>
                            <button className="quantofy-cta-demo-btn">Schedule a Demo</button>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
}
